import { useTranslation } from "react-i18next";
import TrellisLink from "../../core/basic/TrellisLink";
import { useAppSelector } from "../../../redux/hooks";
import { formatCurrency, formatNumber, formatPercent } from "../../../utils/formatNumber";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import { getNTMStatusToggleLabel } from "./getNTMStatusToggleLabel";

const URL_PREFIX = getURLPrefix();

const AdvertisementTitleCell = ({ action, advertisement, children = null }) => {
  const image = `${URL_PREFIX}/api/productimage/?advertisement=${advertisement.id}`;

  return (
    <div className="d-flex">
      <div
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          flexShrink: 0,
          width: 50,
          height: 50,
          marginRight: "10px",
          background: `url(${image}) no-repeat center / contain`,
        }}
        draggable="false"
      />
      <div className="overflow-ellipsis" style={{ textAlign: "left" }}>
        <TrellisLink
          to={`/user/advertising/dashboard/ads/${advertisement.id}`}
          style={{
            textDecoration: "none",
            fontWeight: "500",
            fontSize: "1.5rem",
            cursor: "pointer",
          }}
        >
          {advertisement.name}
        </TrellisLink>{" "}
        {action}
        {children}
      </div>
    </div>
  );
};

const ProductTitleCell = ({ action, product, children = null }) => {
  if (!product) return <>{children}</>;
  const image = `${URL_PREFIX}/api/productimage/?product=${product?.id}`;

  return (
    <div className="d-flex">
      <div
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          flexShrink: 0,
          width: 50,
          height: 50,
          marginRight: "10px",
          background: `url(${image}) no-repeat center / contain`,
        }}
        draggable="false"
      />
      <div className="overflow-ellipsis" style={{ textAlign: "left" }}>
        <TrellisLink
          to={`/user/merchandising/product/${product?.id}`}
          style={{
            textDecoration: "none",
            fontWeight: "500",
            fontSize: "1.5rem",
            cursor: "pointer",
          }}
        >
          {product?.product_title}
        </TrellisLink>{" "}
        {action}
        {children}
      </div>
    </div>
  );
};

const CustomTitleCell = ({ row, children }) => {
  const { advertisement, product, description } = row;

  return (
    <div className="d-flex">
      <div className="overflow-ellipsis" style={{ textAlign: "left" }}>
        {advertisement ?
          <AdvertisementTitleCell advertisement={advertisement} children={children} />
        : product ?
          <ProductTitleCell product={product} children={children} />
        : <span
            style={{
              textDecoration: "none",
              fontWeight: "500",
              fontSize: "1.5rem",
            }}
          >
            {description}
          </span>
        }
      </div>
    </div>
  );
};

const formatMetaValue = (v, key, marketPlace) => {
  const formattingMap = {
    max_acos: "percent",
    target_acos: "percent",
    daily_budget: "currency",
  };
  const format = formattingMap[key];
  switch (format) {
    case "percent":
      return formatPercent(v / 100, {
        decimalPlaces: 0,
      });
    case "currency":
      return formatCurrency(v, {
        marketPlace: marketPlace.marketPlace,
      });
    case "number":
    default:
      return formatNumber(v);
  }
};

export const listMetaUpdates = (t, meta, marketPlace = "USA", textOnly = false) => {
  if (textOnly) {
    return Object.keys(meta)?.map((key) => {
      const { change, current, previous } = meta[key];
      const ntmStatusLabel = getNTMStatusToggleLabel(t, key, current);
      if (ntmStatusLabel) {
        return ntmStatusLabel;
      }
      const metric = snakeCaseToSentenceCase(key).toLowerCase();
      const amount = formatMetaValue(Math.abs(change), key, marketPlace);
      const previousFormatted = formatMetaValue(previous, key, marketPlace);
      const currentFormatted = formatMetaValue(current, key, marketPlace);
      return change > 0 ?
          t(($) => $.annotations["{metric} increased by {amount} from {previous} to {current}"], {
            metric,
            amount,
            previous: previousFormatted,
            current: currentFormatted,
          })
        : t(($) => $.annotations["{metric} decreased by {amount} from {previous} to {current}"], {
            metric,
            amount,
            previous: previousFormatted,
            current: currentFormatted,
          });
    });
  }

  return (
    <ul className="fs-5">
      {Object.keys(meta)?.map((key) => {
        const { change, current, previous } = meta[key];
        const ntmStatusLabel = getNTMStatusToggleLabel(t, key, current);
        if (ntmStatusLabel) {
          return <li key={key}>{ntmStatusLabel}</li>;
        }
        const metric = snakeCaseToSentenceCase(key);
        const amount = formatMetaValue(Math.abs(change), key, marketPlace);
        const previousFormatted = formatMetaValue(previous, key, marketPlace);
        const currentFormatted = formatMetaValue(current, key, marketPlace);
        return (
          <li key={key}>
            {change > 0 ?
              t(($) => $.annotations["{metric} increased by {amount} from {previous} to {current}"], {
                metric,
                amount,
                previous: previousFormatted,
                current: currentFormatted,
              })
            : t(($) => $.annotations["{metric} decreased by {amount} from {previous} to {current}"], {
                metric,
                amount,
                previous: previousFormatted,
                current: currentFormatted,
              })
            }
          </li>
        );
      })}
    </ul>
  );
};

const AnnotationCell = ({ row }) => {
  const { t } = useTranslation(["annotations"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);

  const { annotation_type, meta, advertisement, product, description } = row;

  const renderContentByType = (type) => {
    switch (type) {
      case "AD_CREATED":
        return <AdvertisementTitleCell action={t(($) => $.annotations["created"])} advertisement={advertisement} />;
      case "AD_UPDATED":
        return (
          <AdvertisementTitleCell
            action={t(($) => $.annotations["updated"])}
            advertisement={advertisement}
            children={listMetaUpdates(t, meta, marketPlace)}
          />
        );
      case "PRODUCT_CONTENT_CHANGE":
        return (
          <ProductTitleCell product={product}>
            <br />
            {t(($) => $.annotations["Content Updated"])}
          </ProductTitleCell>
        );
      case "PROMO_STARTED":
        return (
          <ProductTitleCell product={product}>
            <br />
            {t(($) => $.annotations["Promotion Started"])}
          </ProductTitleCell>
        );
      case "PROMO_ENDED":
        return (
          <ProductTitleCell product={product}>
            <br />
            {t(($) => $.annotations["Promotion Ended"])}
          </ProductTitleCell>
        );
      case "CUSTOM":
        return (
          <CustomTitleCell row={row}>
            <br />
            {description}
          </CustomTitleCell>
        );
      default:
        return (
          <div>
            <h4>{t(($) => $.annotations["Other"])}</h4>
          </div>
        );
    }
  };

  return <>{renderContentByType(annotation_type)}</>;
};

export default AnnotationCell;
